import * as React from 'react';
import { DEFAULT_MISTAKE_RULES, MISTAKE_RULE_LIMITS, MISTAKE_RULE_NAMES, normalizeMistakeRules, sameMistakeRules, type MistakeRule, type MistakeRules } from '@chess/api-model';

// The settings section for the blunder and miss rules, drawn from props only:
// the saved rules, where a save stands, and what to do on save. It keeps the
// values being typed itself, and starts again from the saved rules whenever
// they change. An accordion (a native <details>), closed by default: most
// readers keep the defaults, so the form stays out of the way until opened.

export type SaveStatus = 'loading' | 'idle' | 'saving' | 'saved' | 'error';

export interface MistakeRulesFormProps {
  /** The saved rules; undefined while they are read. */
  rules?: MistakeRules;
  status: SaveStatus;
  error?: string;
  onSave(rules: MistakeRules): void;
  /** Whether the accordion starts open; closed by default. */
  defaultOpen?: boolean;
}

interface Field {
  name: MistakeRule;
  label: string;
  help: string;
}

const GROUPS: Array<{ title: string; fields: Field[] }> = [
  {
    title: 'Blunders',
    fields: [{ name: 'blunderPawns', label: 'Blunder', help: 'A move that gives up at least this much against the engine’s best move.' }],
  },
  {
    title: 'Misses',
    fields: [
      { name: 'missGapPawns', label: 'Clear winning move', help: 'A miss: the engine’s best move was at least this far ahead of its second choice, and you played something else.' },
      { name: 'missHoldPawns', label: 'Still standing after a miss', help: 'After a missed win you stand at least this well; worse than that, the move counts as a blunder.' },
    ],
  },
  {
    title: 'Winning positions',
    fields: [
      { name: 'winningPawns', label: 'Winning', help: 'Being this far ahead counts as a winning position.' },
      { name: 'heldPawns', label: 'Win thrown away below', help: 'From a winning position, a blunder or miss that leaves you under this throws the win away.' },
    ],
  },
];

const text = (n: number) => n.toFixed(1);
const textsOf = (rules: MistakeRules): Record<MistakeRule, string> => {
  const out = {} as Record<MistakeRule, string>;
  for (const n of MISTAKE_RULE_NAMES) out[n] = text(rules[n]);
  return out;
};

/** Whether a typed value is a number inside the rule's limits. */
function valid(name: MistakeRule, value: string): boolean {
  const n = Number(value);
  const { min, max } = MISTAKE_RULE_LIMITS[name];
  return value.trim() !== '' && Number.isFinite(n) && n >= min && n <= max;
}

export const MistakeRulesForm: React.FC<MistakeRulesFormProps> = ({ rules, status, error, onSave, defaultOpen = false }) => {
  const [draft, setDraft] = React.useState(() => textsOf(rules ?? DEFAULT_MISTAKE_RULES));
  React.useEffect(() => {
    if (rules) setDraft(textsOf(rules));
  }, [rules]);

  const loading = status === 'loading' || !rules;
  const allValid = MISTAKE_RULE_NAMES.every((n) => valid(n, draft[n]));
  const typed = allValid ? normalizeMistakeRules(draft) : undefined;
  const dirty = !!rules && !!typed && !sameMistakeRules(typed, rules);
  const atDefaults = MISTAKE_RULE_NAMES.every((n) => Number(draft[n]) === DEFAULT_MISTAKE_RULES[n]);
  const orderWrong = allValid && Number(draft.heldPawns) >= Number(draft.winningPawns);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (typed && dirty && !orderWrong) onSave(typed);
  };

  return (
    <details className="panel settings-mistakes" aria-busy={loading} open={defaultOpen || undefined}>
      <summary className="settings-mistakes-summary">
        <h2>Blunders and misses</h2>
        <svg className="settings-mistakes-caret" viewBox="0 0 256 256" aria-hidden="true">
          <path d="M213.66 101.66l-80 80a8 8 0 0 1-11.32 0l-80-80a8 8 0 0 1 11.32-11.32L128 164.69l74.34-74.35a8 8 0 0 1 11.32 11.32Z" />
        </svg>
      </summary>
      <p className="settings-mistakes-intro">
        How the review, the dashboards and the Blunders and Misses screens decide what counts. All values are in pawns, from your side. A change applies to every game at once; nothing is scanned again.
      </p>
      <form className="settings-mistakes-form" onSubmit={submit}>
        {GROUPS.map((group) => (
          <fieldset key={group.title} className="settings-mistakes-group" disabled={loading}>
            <legend>{group.title}</legend>
            {group.fields.map(({ name, label, help }) => {
              const { min, max, step } = MISTAKE_RULE_LIMITS[name];
              const id = `settings-mistakes-${name}`;
              const bad = !valid(name, draft[name]);
              return (
                <div key={name} className="settings-mistakes-field">
                  <label htmlFor={id}>{label}</label>
                  <span className="settings-mistakes-input">
                    <input
                      id={id}
                      className="input"
                      type="number"
                      inputMode="decimal"
                      min={min}
                      max={max}
                      step={step}
                      value={draft[name]}
                      aria-invalid={bad || undefined}
                      aria-describedby={`${id}-help`}
                      onChange={(e) => setDraft({ ...draft, [name]: e.target.value })}
                    />
                    <span className="settings-mistakes-unit">pawns</span>
                  </span>
                  <p id={`${id}-help`} className="settings-mistakes-help">
                    {help} <span className="settings-mistakes-default">Default {text(DEFAULT_MISTAKE_RULES[name])}.</span>
                    {bad && <span className="settings-mistakes-error"> Between {text(min)} and {text(max)}.</span>}
                  </p>
                </div>
              );
            })}
          </fieldset>
        ))}
        {orderWrong && <p className="settings-mistakes-error">"Win thrown away below" must be under "Winning".</p>}
        <div className="settings-mistakes-actions">
          <button type="submit" className="btn btn-primary" disabled={loading || !dirty || orderWrong || status === 'saving'}>
            Save
          </button>
          <button type="button" className="btn btn-secondary" disabled={loading || atDefaults || status === 'saving'} onClick={() => setDraft(textsOf(DEFAULT_MISTAKE_RULES))}>
            Restore defaults
          </button>
          <span className="settings-mistakes-status" data-status={status} role="status">
            {status === 'loading' && 'Loading…'}
            {status === 'saving' && 'Saving…'}
            {status === 'saved' && !dirty && 'Saved'}
            {status === 'error' && (error ?? 'Could not save')}
          </span>
        </div>
      </form>
    </details>
  );
};
